import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { scanDuplicates, deleteStudent, deleteStaff } from "../../lib/api";

const REASON_LABELS = {
  name: "Name",
  dob: "DOB",
  contact_name: "Contact name",
  contact_phone: "Contact phone",
  contact_email: "Contact email",
  email: "Email",
  personal_phone: "Personal phone",
  work_phone: "Work phone",
  phone: "Phone",
};

function MatchBadges({ reasons }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {reasons.map((r) => (
        <Badge key={r} variant="secondary" className="text-xs" data-testid={`match-${r}`}>
          {REASON_LABELS[r] || r}
        </Badge>
      ))}
    </div>
  );
}

function StudentCard({ record, onDelete, onView }) {
  const isArchived = record._source === "archived";
  return (
    <div className="border rounded-lg p-3 bg-white space-y-1.5 text-sm" data-testid={`student-dup-${record.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold">
          {record.first_name} {record.last_name}
          {isArchived && <Badge variant="outline" className="ml-2 text-[10px]">Archived</Badge>}
          {record.status === "former" && !isArchived && <Badge variant="outline" className="ml-2 text-[10px]">Former</Badge>}
        </div>
      </div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {record.dob && <div>DOB: {record.dob}</div>}
        {record.school_district && <div>District: {record.school_district}</div>}
        {record.first_contact_name && <div>Contact: {record.first_contact_name}</div>}
        {record.first_contact_phone && <div>Phone: {record.first_contact_phone}</div>}
        {record.first_contact_email && <div>Email: {record.first_contact_email}</div>}
        {record.student_number && <div>Student #: {record.student_number}</div>}
      </div>
      <div className="flex gap-2 pt-2">
        <Button size="sm" variant="outline" onClick={() => onView(record.id)} data-testid={`view-student-${record.id}`}>View</Button>
        {!isArchived && (
          <DeleteConfirm
            title={`Delete ${record.first_name} ${record.last_name}?`}
            description="This permanently removes the student record. This cannot be undone."
            onConfirm={() => onDelete(record.id)}
            testId={`delete-student-${record.id}`}
          />
        )}
      </div>
    </div>
  );
}

function StaffCard({ record, onDelete }) {
  return (
    <div className="border rounded-lg p-3 bg-white space-y-1.5 text-sm" data-testid={`staff-dup-${record.id}`}>
      <div className="font-semibold">{record.name}</div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {record.email && <div>Email: {record.email}</div>}
        {record.personal_phone && <div>Personal: {record.personal_phone}</div>}
        {record.work_phone && <div>Work: {record.work_phone}</div>}
        {record.dob && <div>DOB: {record.dob}</div>}
        {record.positions?.length > 0 && <div>Positions: {record.positions.join(", ")}</div>}
      </div>
      <div className="flex gap-2 pt-2">
        <DeleteConfirm
          title={`Delete ${record.name}?`}
          description="This permanently removes the staff record (including login). This cannot be undone."
          onConfirm={() => onDelete(record.id)}
          testId={`delete-staff-${record.id}`}
        />
      </div>
    </div>
  );
}

function ContactCard({ record, onView }) {
  return (
    <div className="border rounded-lg p-3 bg-white space-y-1.5 text-sm" data-testid={`contact-dup-${record.student_id}-${record.slot}`}>
      <div className="font-semibold">{record.contact_name}</div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {record.contact_relationship && <div>Relationship: {record.contact_relationship}</div>}
        {record.contact_phone && <div>Phone: {record.contact_phone}</div>}
        {record.contact_email && <div>Email: {record.contact_email}</div>}
        <div>On student: <span className="font-medium text-foreground">{record.student_name}</span></div>
        <div className="text-[10px] uppercase tracking-wide">{record.slot} contact</div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button size="sm" variant="outline" onClick={() => onView(record.student_id)} data-testid={`view-contact-student-${record.student_id}`}>
          Open student
        </Button>
      </div>
    </div>
  );
}

function DeleteConfirm({ title, description, onConfirm, testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="destructive" data-testid={testId}>Delete</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid={`${testId}-confirm`}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function DuplicatesFinder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const handleScan = async () => {
    setLoading(true);
    try {
      const res = await scanDuplicates();
      setData(res.data);
      const total =
        res.data.totals.student_groups +
        res.data.totals.staff_groups +
        res.data.totals.contact_groups;
      toast.success(total === 0 ? "No duplicates found." : `Found ${total} duplicate group(s).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to scan for duplicates");
    } finally {
      setLoading(false);
    }
  };

  const removeStudentLocally = (id) => {
    setData((prev) =>
      !prev
        ? prev
        : {
            ...prev,
            students: prev.students
              .map((g) => ({ ...g, records: g.records.filter((r) => r.id !== id) }))
              .filter((g) => g.records.length > 1),
            totals: {
              ...prev.totals,
              student_groups: prev.students
                .map((g) => ({ ...g, records: g.records.filter((r) => r.id !== id) }))
                .filter((g) => g.records.length > 1).length,
            },
          },
    );
  };

  const removeStaffLocally = (id) => {
    setData((prev) =>
      !prev
        ? prev
        : {
            ...prev,
            staff: prev.staff
              .map((g) => ({ ...g, records: g.records.filter((r) => r.id !== id) }))
              .filter((g) => g.records.length > 1),
            totals: {
              ...prev.totals,
              staff_groups: prev.staff
                .map((g) => ({ ...g, records: g.records.filter((r) => r.id !== id) }))
                .filter((g) => g.records.length > 1).length,
            },
          },
    );
  };

  const handleDeleteStudent = async (id) => {
    try {
      await deleteStudent(id);
      removeStudentLocally(id);
      toast.success("Student deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete student");
    }
  };

  const handleDeleteStaff = async (id) => {
    try {
      await deleteStaff(id);
      removeStaffLocally(id);
      toast.success("Staff deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete staff");
    }
  };

  const studentGroups = data?.students || [];
  const staffGroups = data?.staff || [];
  const contactGroups = data?.contacts || [];

  return (
    <Card data-testid="duplicates-finder-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle>Duplicate Records</CardTitle>
            <CardDescription>
              Scans students (current + archived), staff, and parent contacts. Records sharing two or more
              matching fields (name, DOB, phone, email) are flagged for admin review.
            </CardDescription>
          </div>
          <Button onClick={handleScan} disabled={loading} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="scan-duplicates-btn">
            {loading ? "Scanning..." : data ? "Rescan" : "Scan for Duplicates"}
          </Button>
        </div>
      </CardHeader>
      {data && (
        <CardContent>
          <Tabs defaultValue="students">
            <TabsList>
              <TabsTrigger value="students" data-testid="dup-tab-students">
                Students ({studentGroups.length})
              </TabsTrigger>
              <TabsTrigger value="staff" data-testid="dup-tab-staff">
                Staff ({staffGroups.length})
              </TabsTrigger>
              <TabsTrigger value="contacts" data-testid="dup-tab-contacts">
                Contacts ({contactGroups.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="students" className="space-y-4 pt-4">
              {studentGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No duplicate students detected.</p>
              ) : (
                studentGroups.map((g, i) => (
                  <div key={`student-group-${i}-${g.records[0]?.id}`} className="border-l-4 border-amber-400 bg-amber-50/40 p-3 rounded-r-lg space-y-3">
                    <MatchBadges reasons={g.match_reasons} />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {g.records.map((r) => (
                        <StudentCard
                          key={`${r.id}-${r._source}`}
                          record={r}
                          onDelete={handleDeleteStudent}
                          onView={(id) => navigate(`/students/${id}`)}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="staff" className="space-y-4 pt-4">
              {staffGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No duplicate staff detected.</p>
              ) : (
                staffGroups.map((g, i) => (
                  <div key={`staff-group-${i}-${g.records[0]?.id}`} className="border-l-4 border-amber-400 bg-amber-50/40 p-3 rounded-r-lg space-y-3">
                    <MatchBadges reasons={g.match_reasons} />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {g.records.map((r) => (
                        <StaffCard key={r.id} record={r} onDelete={handleDeleteStaff} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="contacts" className="space-y-4 pt-4">
              {contactGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No duplicate parent contacts detected.</p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Parent/guardian contacts repeated across multiple student records. These are commonly siblings —
                    open the student record to edit or remove the contact manually if the entry is redundant.
                  </p>
                  {contactGroups.map((g, i) => (
                    <div key={`contact-group-${i}-${g.records[0]?.student_id}-${g.records[0]?.slot}`} className="border-l-4 border-amber-400 bg-amber-50/40 p-3 rounded-r-lg space-y-3">
                      <MatchBadges reasons={g.match_reasons} />
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {g.records.map((r) => (
                          <ContactCard
                            key={`${r.student_id}-${r.slot}`}
                            record={r}
                            onView={(id) => navigate(`/students/${id}`)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      )}
    </Card>
  );
}
